import { useState, useEffect, useCallback, useRef } from 'react'
import { Aircraft } from '../types/intellijetnet'
import { OffMarket } from '../types/database'
import { OffMarketService } from '../services/off-market.service'
import { AircraftService } from '../services/intellijetnet/aircraft.service'
import { SoldAircraftItem } from './useMarketSoldData'

export interface OffMarketAircraftItem {
  aircraft: Aircraft
  offMarket: OffMarket
}

interface UseMarketOffMarketParams {
  modelId: string | undefined
  aircraft: Aircraft[]
  recentlySoldAircraft: SoldAircraftItem[]
  onRefreshData: () => Promise<void>
  onError: (message: string) => void
}

export function useMarketOffMarket({
  modelId,
  aircraft,
  recentlySoldAircraft,
  onRefreshData,
  onError,
}: UseMarketOffMarketParams) {
  const [offMarketAircraftData, setOffMarketAircraftData] = useState<OffMarketAircraftItem[]>([])
  const [showAircraftSearchModal, setShowAircraftSearchModal] = useState(false)
  const [selectedAircraftForForm, setSelectedAircraftForForm] = useState<Aircraft | null>(null)
  const cleanedUpOffMarketAircraftRef = useRef<Set<number>>(new Set())

  const fetchOffMarketData = useCallback(async () => {
    try {
      const numericModelId = parseInt(modelId!, 10)
      if (isNaN(numericModelId)) return

      const offMarketData = await OffMarketService.getByModelId(numericModelId)

      if (offMarketData.length === 0) {
        setOffMarketAircraftData([])
        return
      }

      const aircraftDetailsPromises = offMarketData.map(async (offMarket) => {
        try {
          const ac = await AircraftService.getById(offMarket.aircraft_id)
          if (!ac) {
            console.warn(`[Off-Market] Aircraft ${offMarket.aircraft_id} not found in IntelliJetNet API`)
            return null
          }
          return { aircraft: ac, offMarket }
        } catch (err) {
          console.warn(`[Off-Market] Error fetching aircraft ${offMarket.aircraft_id} from API:`, err)
          return null
        }
      })

      const aircraftDetails = await Promise.all(aircraftDetailsPromises)
      const validAircraftDetails = aircraftDetails.filter(
        (item): item is OffMarketAircraftItem => item !== null
      )

      setOffMarketAircraftData(validAircraftDetails)
    } catch (err: any) {
      // Don't throw here to avoid breaking the entire page
    }
  }, [modelId])

  // Auto-cleanup: When an off-market "for_sale" aircraft gets a NEW transaction (sold via API
  // AFTER the off-market entry was created), automatically delete the off-market entry.
  useEffect(() => {
    const cleanupSoldOffMarketAircraft = async () => {
      const soldAircraftTransactionDates = new Map<number, string>()
      for (const item of recentlySoldAircraft) {
        const transDate = item.aircraftTransaction?.transaction_date || item.transaction?.trans_date
        if (transDate) {
          soldAircraftTransactionDates.set(item.aircraft.aircraft_id, transDate)
        }
      }

      const offMarketForSaleWithNewTransactions = offMarketAircraftData.filter(item => {
        if (item.offMarket.market_category !== 'for_sale') return false
        if (cleanedUpOffMarketAircraftRef.current.has(item.aircraft.aircraft_id)) return false

        const transDate = soldAircraftTransactionDates.get(item.aircraft.aircraft_id)
        if (!transDate) return false

        const offMarketCreatedAt = new Date(item.offMarket.created_at).getTime()
        const transactionDate = new Date(transDate).getTime()
        return transactionDate > offMarketCreatedAt
      })

      if (offMarketForSaleWithNewTransactions.length === 0) return

      for (const item of offMarketForSaleWithNewTransactions) {
        try {
          await OffMarketService.delete(item.aircraft.aircraft_id.toString())
          cleanedUpOffMarketAircraftRef.current.add(item.aircraft.aircraft_id)
          console.log(`[Off-Market Cleanup] Removed off-market entry for aircraft ${item.aircraft.aircraft_id} (sold via API transaction after off-market entry was created)`)
        } catch (error) {
          console.error(`[Off-Market Cleanup] Failed to cleanup off-market entry for aircraft ${item.aircraft.aircraft_id}:`, error)
        }
      }

      setOffMarketAircraftData(prev =>
        prev.filter(item => !cleanedUpOffMarketAircraftRef.current.has(item.aircraft.aircraft_id))
      )
    }

    if (offMarketAircraftData.length > 0 && recentlySoldAircraft.length > 0) {
      cleanupSoldOffMarketAircraft()
    }
  }, [offMarketAircraftData, recentlySoldAircraft])

  // Auto-cleanup: When an off-market "for_sale" aircraft appears in the API as actively for sale
  useEffect(() => {
    const cleanupOffMarketWhenApiForSale = async () => {
      const apiForSaleIds = new Set(
        aircraft.filter(a => a.forsale && a.is_active).map(a => a.aircraft_id)
      )

      const offMarketNowApiForSale = offMarketAircraftData.filter(item =>
        item.offMarket.market_category === 'for_sale' &&
        apiForSaleIds.has(item.aircraft.aircraft_id) &&
        !cleanedUpOffMarketAircraftRef.current.has(item.aircraft.aircraft_id)
      )

      if (offMarketNowApiForSale.length === 0) return

      for (const item of offMarketNowApiForSale) {
        try {
          await OffMarketService.delete(item.aircraft.aircraft_id.toString())
          cleanedUpOffMarketAircraftRef.current.add(item.aircraft.aircraft_id)
          console.log(`[Off-Market Cleanup] Removed off-market entry for aircraft ${item.aircraft.aircraft_id} (now for sale in API)`)
        } catch (error) {
          console.error(`[Off-Market Cleanup] Failed to cleanup off-market entry for aircraft ${item.aircraft.aircraft_id}:`, error)
        }
      }

      setOffMarketAircraftData(prev =>
        prev.filter(item => !cleanedUpOffMarketAircraftRef.current.has(item.aircraft.aircraft_id))
      )
    }

    if (offMarketAircraftData.length > 0 && aircraft.length > 0) {
      cleanupOffMarketWhenApiForSale()
    }
  }, [offMarketAircraftData, aircraft])

  const handleSelectAircraft = useCallback((ac: Aircraft) => {
    setSelectedAircraftForForm(ac)
    setShowAircraftSearchModal(false)
  }, [])

  const handleOffMarketSuccess = useCallback(async () => {
    try {
      setSelectedAircraftForForm(null)
      await fetchOffMarketData()
      await onRefreshData()
    } catch (err: any) {
      onError(err.message || 'Failed to refresh data')
    }
  }, [fetchOffMarketData, onRefreshData, onError])

  const handleOffMarketCancel = useCallback(() => {
    setSelectedAircraftForForm(null)
  }, [])

  return {
    offMarketAircraftData,
    showAircraftSearchModal,
    setShowAircraftSearchModal,
    selectedAircraftForForm,
    fetchOffMarketData,
    handleSelectAircraft,
    handleOffMarketSuccess,
    handleOffMarketCancel,
  }
}
